import { useEffect } from 'react';

import { Card, Steps } from 'antd';
import { OrderDetailGrid } from 'components/ArticleOrder';
import { useGetAfterCopyRightAssignmentQuery, useGetCurrentUserQuery } from 'services';
import { CircularProgress } from 'components';
import MediaField from 'components/AfterCopyright/MediaField';
import ContentAsignment from 'components/AfterCopyright/ContentAsignment';
import StatusBar from 'components/StatusBar';
import { OrderDetailDescriptions } from 'components/ArticleOrder/OrderDetailDescriptions';
import ActivityHistory from 'components/ActivityHistory';
import { article_order_status } from 'constant/static';
import { useMediaOfferDecisionGuard, useMediaOfferPermission } from 'hooks';
import { decidableCommitteeStrings } from 'routes/article-detail';
import ArticleOrderMediaWrapper from 'components/ArticleOrderMedia/ArticleOrderMediaWrapper';

import ComitteeDescriptionArea from '../ComitteeDescriptionArea';
import LastRedactionOpinion from '../LastRedactionOpinion';

import ActionButtons from './ActionButtons';
import useStepController from './useStepController';

const AfterCopyrightProcessContent = ({ offer, type, committee, disabledStatus }) => {
  const { data: current_user } = useGetCurrentUserQuery();
  const { id: currentUserID, isAdmin, euManager, pubManager } = current_user;

  const { current, selectedItemStatus, isLoading, handleCurrentIndex, currentIndex, userCanDecide, afterCopyrightSteps } = useStepController({
    type,
    offerID: offer && offer.id,
    offerStatus: offer.status,
    currentUserID,
    isAdmin,
    disabledStatus,
  });

  // Medya yetkisi TAMAMEN hook'tan gelir: atama kimliğini de kendisi çözer (bkz. useMediaOfferPermission
  // tasarım kuralı 2). Aşama çubuğunda seçili adım (`current`) yetkiyi etkilemez — kullanıcı geçmiş bir
  // adımı görüntülemek için tıkladığında medya aksiyonları kaybolmaz.
  const mediaOfferPermission = useMediaOfferPermission(offer);

  // Karar zorunlu aşamada "tüm medya tekliflerine karar verilmiş olma" kuralı: engel varsa gerekçe
  // `MediaField`'ın "Gönder" butonuna tooltip olarak gider ve buton kapanır. Zorunluluk
  // `decisionRequired`'dan gelir (tek kaynak `STATUS_RULE`); karar opsiyonel aşamalarda kilit yoktur.
  const { blockReason: mediaDecisionBlockReason } = useMediaOfferDecisionGuard({
    offerID: offer?.id,
    activeDecider: mediaOfferPermission.activeDecider,
    decisionRequired: mediaOfferPermission.decisionRequired,
    canManage: mediaOfferPermission.canManage,
  });

  const { data: afterCopyright } = useGetAfterCopyRightAssignmentQuery({
    articleOffer: offer && offer.id,
    type: type,
    ...((offer.status === 27 || offer.status === 39) && { onlyActive: true }),
  });

  useEffect(() => {
    if (document) {
      // buradaki id degeri /src/routes/index.js dosyasindaki div in id degerini ifade ediyor
      const element = document.getElementById('routes-wrapper');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [offer.status]);

  if (isLoading) return <CircularProgress />;

  const showActionButtons = () => {
    if (offer.decidableCommittee === decidableCommitteeStrings.eu) {
      return isAdmin || euManager;
    }
    if (offer.decidableCommittee === decidableCommitteeStrings.pub) {
      return isAdmin || pubManager;
    }
  };

  // Atama bloğu (`ContentAsignment`) madde dosyalarını kendi basar; görünürken tablo onun içine gömülür,
  // aksi halde dosyalar iki kez çıkar.
  const isAssignmentVisible = current === 0 && showActionButtons();

  // Medya maddeye aittir, seçili adıma değil: atama statülerinde (27, 39) ve çubukta "Madde Atama" adımı
  // seçiliyken de görünür.
  const mediaOfferTable = (
    <ArticleOrderMediaWrapper
      offerID={offer && offer.id}
      permission={mediaOfferPermission}
      isEncylopediaUnitControl
      isCommiteeControlColumnsVisible={!mediaOfferPermission.isReadOnly}
      isContentSend
    />
  );

  return (
    <div className="gx-pb-4">
      <Steps
        className={!article_order_status['Yayin Mudurlugu Madde Atama'] >= offer.status ? 'after-copyright-process-steps' : ''}
        onChange={(current) => handleCurrentIndex(current)}
        current={current}
        labelPlacement="vertical"
        items={afterCopyrightSteps.sort((a, b) => a.status - b.status)}
        progressDot
      />
      <Card className="gx-p-3">
        <StatusBar status={offer && offer.status} />
        {/**
         * Son Redaksiyondan gelen notu görüntülemek icin kullanilir.
         */}
        <LastRedactionOpinion latestRedactionInfos={afterCopyright?.[Number(current) - 1]} />
        <OrderDetailGrid offer={offer} />
        <OrderDetailDescriptions offer={offer && offer} />
        {
          /**
           * Ayk control, eu control ve pub control ve redaksiyon kararı ile gelen Heyet notlari.
           */
          <ComitteeDescriptionArea
            redactionNote={afterCopyright?.[Number(current) - 1]?.note}
            ownerCommiteeId={offer.ownerCommittee.id}
            aykControlDescription={offer.aykControlDescription}
            euControlDescription={offer.euControlDescription}
            pubControlDescription={offer.pubControlDescription}
            ownerCommitteeOpinion={offer.ownerCommitteeOpinion}
          />
        }
        <ActivityHistory
          id={offer && offer.id}
          requestType="post_offer"
        />
        {/*
         * Madde dosyaları + medya teklifi tablosu + (yetki varsa) aşama onay formu. Onay formu yalnız seçili
         * adım aktif statüye denk geldiğinde açılır (`isProcessContinue`); atama adımında `selectedItemStatus`
         * boş olduğundan form basılmaz.
         */}
        {!isAssignmentVisible && (
          <MediaField
            offerID={offer && offer.id}
            isProcessContinue={selectedItemStatus === offer.status}
            isMediaEditable={userCanDecide}
            submitBlockReason={mediaDecisionBlockReason}
          >
            {mediaOfferTable}
          </MediaField>
        )}
        {/* sadece madde atama asamasinda goruntulenecek bir komponent */}
        {isAssignmentVisible && (
          <>
            <ContentAsignment
              offerID={offer && offer.id}
              disabledAsignment={offer.status > disabledStatus}
              offerURI={offer && offer['@id']}
              type={type}
              afterCopyright={afterCopyright}
            >
              {mediaOfferTable}
            </ContentAsignment>
            {/**
             * telif sonrasi sureclerinde asamalardan sonra surecleri baslatmak icin kullanilir
             */}
            {showActionButtons() && (
              <ActionButtons
                showStartProcessButton={offer.status === disabledStatus}
                offerID={offer && offer.id}
                committee={committee}
                offerStatus={offer.status}
                handleCurrentIndex={handleCurrentIndex}
                currentIndex={currentIndex}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default AfterCopyrightProcessContent;
